/**
 * useInterpreterMode — web client, on the SHARED v2 state machine.
 *
 * This file used to be a hand-written port of the RN hook, and it had drifted
 * onto the dead v1 contract: `state · speaker · transcript · partial ·
 * cultural_note · visual_aid · privacy · agent_state`. Only `state` and
 * `transcript` are still emitted by the worker at all, so web rendered
 * finalized pairs and nothing else — no streaming partials (v2 partials go out
 * as `utterance`, and nothing has emitted `interpreter:partial` since the
 * 2026-07-24 redesign), and the equator label was stuck on "Listening" forever
 * because `agent_state` never arrives.
 *
 * All web decision-making now lives in @wg/hooks/interpreter. RN currently
 * implements v2 independently and does not yet import this reducer; converging
 * it is a follow-up. What remains here is only what is genuinely web-specific:
 * the Socket.IO instance, localStorage, matchMedia, and the httpClient call that
 * activates the mode. If you find yourself adding merge or render logic to this
 * file it belongs in the reducer instead.
 *
 * DELIBERATELY DROPPED (the worker does not emit them; keeping client handlers
 * for dead events is what disguised the drift):
 *   culturalNotes · visualAids · agentState · privacyMode · partials ·
 *   onUserSpeech / onAgentSpeech
 * The last two locally synthesised transcript rows from the client's own ASR.
 * v2 sends the speaker's own words back as echo partials, so inventing them
 * client-side would double-render every turn.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';

import {
  INITIAL_INTERPRETER_STATE,
  interpreterReducer,
  orderedUtterances,
  type InterpreterErrorPayload,
  type InterpreterEvent,
  type InterpreterSessionState,
  type InterpreterState as SharedInterpreterState,
  type InterpreterSuggestionPayload,
  type InterpreterUtterance,
  type InterpreterViewMode,
} from '@wg/hooks/interpreter';

import { httpClient } from '../http';

const ONBOARDING_KEY = 'interpreter_onboarding_shown';

function getStoredBoolean(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function setStoredBoolean(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* private mode / quota — a UI hint is not worth throwing over */
  }
}

export interface UseInterpreterModeArgs {
  socketRef?: { current: Socket | null };
  sessionId?: string | null;
  userLanguage: string;
}

export function useInterpreterMode({
  socketRef,
  sessionId,
  userLanguage,
}: UseInterpreterModeArgs) {
  const [shared, setShared] = useState<SharedInterpreterState>(
    INITIAL_INTERPRETER_STATE,
  );
  const [onboardingShown, setOnboardingShown] = useState(() =>
    getStoredBoolean(ONBOARDING_KEY),
  );
  const [situation, setSituation] = useState('');

  // Compact screens start flipped so the person across the table can read.
  const [isCompact, setIsCompact] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(max-height: 750px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-height: 750px)');
    const handler = (e: MediaQueryListEvent) => setIsCompact(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const dispatch = useCallback(
    (event: InterpreterEvent) =>
      setShared(prev => interpreterReducer(prev, event)),
    [],
  );

  // ── Socket wiring ─────────────────────────────────────────────────────
  // The socket instance is swapped underneath us WITHOUT a re-render (the
  // caller holds it in a ref), so re-attachment is polled. Kept from the
  // previous implementation deliberately — it is load-bearing, not incidental.
  const subscribedRef = useRef<Socket | null>(null);

  useEffect(() => {
    const onSession = (p: InterpreterSessionState) =>
      dispatch({ type: 'session', payload: p });
    const onUtterance = (p: InterpreterUtterance) =>
      dispatch({ type: 'utterance', payload: p });
    const onError = (p: InterpreterErrorPayload) =>
      dispatch({ type: 'error', payload: p });
    const onSuggestion = (p: InterpreterSuggestionPayload) =>
      dispatch({ type: 'suggestion', payload: p });

    const detach = (s: Socket | null) => {
      if (!s) return;
      s.off('interpreter:session', onSession);
      s.off('interpreter:utterance', onUtterance);
      s.off('interpreter:error', onError);
      s.off('interpreter:suggestion', onSuggestion);
    };

    const attach = () => {
      const socket = socketRef?.current ?? null;
      if (socket === subscribedRef.current) return;
      detach(subscribedRef.current);
      subscribedRef.current = socket;
      if (!socket) return;
      socket.on('interpreter:session', onSession);
      socket.on('interpreter:utterance', onUtterance);
      socket.on('interpreter:error', onError);
      socket.on('interpreter:suggestion', onSuggestion);
    };

    attach();
    const interval = setInterval(attach, 500);
    return () => {
      clearInterval(interval);
      detach(subscribedRef.current);
      subscribedRef.current = null;
    };
  }, [socketRef, dispatch]);

  // ── Activation ────────────────────────────────────────────────────────
  const activate = useCallback(
    async (newLocalLang: string, newSituation?: string) => {
      if (newSituation) setSituation(newSituation);
      if (!sessionId) return false;
      try {
        await httpClient.patch(`/sessions/v1/${sessionId}`, {
          interpreterMode: {
            active: true,
            localLanguage: newLocalLang,
            situation: newSituation || 'Manual activation',
          },
        });
        return true;
      } catch {
        // The worker also drives activation from the voice handoff, so a failed
        // PATCH is not necessarily fatal — but we do NOT fake `active` locally.
        // The HUD opens when `interpreter:session` says it is open, and not
        // before; optimistically flipping it is how a blank HUD looked "on".
        return false;
      }
    },
    [sessionId],
  );

  const deactivate = useCallback(async () => {
    dispatch({ type: 'reset' });
    if (!sessionId) return;
    try {
      await httpClient.patch(`/sessions/v1/${sessionId}`, {
        interpreterMode: { active: false },
      });
    } catch {
      /* HUD is already closed locally; the worker exits on its own too */
    }
  }, [sessionId, dispatch]);

  const toggleFlip = useCallback(() => {
    setShared(prev => {
      const next: InterpreterViewMode =
        prev.viewMode === 'split'
          ? 'flip-user'
          : prev.viewMode === 'flip-user'
            ? 'flip-local'
            : 'split';
      return interpreterReducer(prev, { type: 'setViewMode', payload: next });
    });
  }, []);

  const setViewMode = useCallback(
    (m: InterpreterViewMode) => dispatch({ type: 'setViewMode', payload: m }),
    [dispatch],
  );

  const markOnboardingShown = useCallback(() => {
    setStoredBoolean(ONBOARDING_KEY, true);
    setOnboardingShown(true);
  }, []);

  const utterances = useMemo(() => orderedUtterances(shared), [shared]);

  // Compact screens default to flipped, but never override an explicit choice.
  const viewMode: InterpreterViewMode =
    shared.viewMode === 'split' && isCompact ? 'flip-user' : shared.viewMode;

  const state = {
    active: shared.active,
    session: shared.session,
    userLang: shared.session?.pair.traveler ?? userLanguage,
    localLang: shared.session?.pair.local ?? '',
    situation,
    viewMode,
    utterances,
    lastError: shared.lastError,
    suggestion: shared.suggestion,
    isCompact,
    onboardingShown,
  };

  return {
    state,
    activate,
    deactivate,
    setViewMode,
    toggleFlip,
    markOnboardingShown,
    dismissSuggestion: () => dispatch({ type: 'dismissSuggestion' }),
  };
}

/** The context provider types against this, so it stays exported by name. */
export type UseInterpreterModeReturn = ReturnType<typeof useInterpreterMode>;

export default useInterpreterMode;
